import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';
import { PaymentInfoModalButton, InfoBanner, Button } from '@spotahome/ui-library';
import { DSIcon } from '@spotahome/ui-library/base';
import ListingTitle from '@spotahome/ui-library/src/tenant/components/ListingTitle';
import { Mediator } from '@spotahome/soyuz-mediator';

import ListRow from '@spotahome/marketplace-common/src/components/ListRow';
import { usePricingContext } from '@spotahome/marketplace-common/src/context/PricingContext';
import {
  OPEN_BOOKNOW_DATEPICKER_DESKTOP,
  OPEN_BOOKNOW_DATEPICKER_MOBILE
} from '@spotahome/marketplace-common/src/channels';

import { ListingSectionObservable } from '../ListingSection';

import './ListingPaymentsModule.scss';

const ListingPaymentsModule = ({ contractType, hasDates, deviceType }) => {
  const { priceRange, formatPrice } = usePricingContext();

  const { seasonalPrice } = priceRange;
  const isSeasonal = !!seasonalPrice;
  const priceAmount = seasonalPrice?.min ?? priceRange.amount;

  const displayPrice = `${isSeasonal ? `${trans('rentable_unit_price.label.from')} ` : ''}${formatPrice(priceAmount)} ${trans('property_show.book_now_card.per_month')}`;

  const handleAddDatesClick = () => {
    const channel =
      deviceType === 'smartphone' || deviceType === 'tablet'
        ? OPEN_BOOKNOW_DATEPICKER_MOBILE
        : OPEN_BOOKNOW_DATEPICKER_DESKTOP;
    Mediator.publish(channel);
  };

  const getSeasonalBannerContent = () => {
    if (!isSeasonal) return null;

    if (!hasDates) {
      return (
        <>
          <span>{trans('listing.payments-module.seasonal.no-dates')}</span>
          <div>
            <Button color="link" size="small" onClick={handleAddDatesClick}>
              {trans('listing.payments-module.add-dates')}
            </Button>
          </div>
        </>
      );
    }

    return <span>{trans('listing.payments-module.seasonal.with-dates')}</span>;
  };

  const seasonalBannerContent = getSeasonalBannerContent();

  return (
    <ListingSectionObservable section="pricing">
      <div className="listing-payments-module">
        <ListingTitle role="secondary">
          {trans('listing.pricing.title.standalone')}
        </ListingTitle>
        <ListRow
          withBorder={false}
          renderLeftBlock={() => (
            <p>{trans('listing.pricing.monthly-rent')}</p>
          )}
          renderRightBlock={() => (
            <p className="listing-payments-module__price">{displayPrice}</p>
          )}
        />
        <ListRow
          withBorder={false}
          renderLeftBlock={() => (
            <p>{trans('listing.pricing.details.type-of-contract')}</p>
          )}
          renderRightBlock={() => (
            <p>{trans(`contract_type.label.${contractType}`)}</p>
          )}
        />
        <PaymentInfoModalButton
          linkText={trans('listing.payments-module.view-all-payments')}
          linkColor="link"
          linkSize="small"
          linkClassName="listing-payments-module__cta"
          dataTest="listing-payments-module-view-all"
        />
        {seasonalBannerContent ? (
          <InfoBanner
            className="listing-payments-module__banner"
            theme="info"
            closable={false}
            icon={<DSIcon name="info" size="m" />}
            content={seasonalBannerContent}
          />
        ) : null}
      </div>
    </ListingSectionObservable>
  );
};

ListingPaymentsModule.propTypes = {
  contractType: PropTypes.string.isRequired,
  hasDates: PropTypes.bool,
  deviceType: PropTypes.oneOf(['smartphone', 'tablet', 'desktop']).isRequired
};

export default ListingPaymentsModule;
